import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, MapPin, Phone } from 'lucide-react'
import { initialLanguage, manualLanguage, chooseLanguage, normalizeLanguage, resolveLanguage, type SupportedLanguage } from '@/i18n/languagePolicy'
import { empresaApi, filialApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'

type FlagCountry = 'BR' | 'US' | 'ES'

const languages: Array<{ code: SupportedLanguage; country: FlagCountry; label: string }> = [
  { code: 'pt-BR', country: 'BR', label: 'Português' },
  { code: 'en-US', country: 'US', label: 'English' },
  { code: 'es-ES', country: 'ES', label: 'Español' }
]

function FlagIcon({ country }: { country: FlagCountry }) {
  if (country === 'BR') {
    return (
      <svg viewBox="0 0 28 20" className="h-5 w-7" role="img" aria-label="Brasil">
        <rect width="28" height="20" rx="1" fill="#009B3A" />
        <path d="M14 2.5 25 10 14 17.5 3 10Z" fill="#FFDF00" />
        <circle cx="14" cy="10" r="4.2" fill="#002776" />
        <path d="M10.3 9.1c2.7-.8 5.3-.4 7.6 1" fill="none" stroke="#fff" strokeWidth=".8" />
      </svg>
    )
  }

  if (country === 'US') {
    return (
      <svg viewBox="0 0 28 20" className="h-5 w-7" role="img" aria-label="United States">
        <rect width="28" height="20" rx="1" fill="#fff" />
        {[0, 4, 8, 12, 16].map((y) => <rect key={y} y={y} width="28" height="2" fill="#B22234" />)}
        <rect width="12" height="10.8" fill="#3C3B6E" />
        {[2, 6, 10].flatMap((x) => [2, 5, 8].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r=".65" fill="#fff" />
        )))}
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 28 20" className="h-5 w-7" role="img" aria-label="España">
      <rect width="28" height="20" rx="1" fill="#AA151B" />
      <rect y="5" width="28" height="10" fill="#F1BF00" />
      <rect x="7" y="7.5" width="2.2" height="5" rx=".4" fill="#AA151B" />
    </svg>
  )
}

export default function BrandTopBar() {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language) ?? initialLanguage()

  useEffect(() => {
    let cancelled = false
    void resolveLanguage(user, {
      manual: manualLanguage,
      browser: initialLanguage,
      filial: async (id) => (await filialApi.get(id)).data,
      empresa: async (id) => (await empresaApi.get(id)).data
    }).then((language) => {
      if (!cancelled) void i18n.changeLanguage(manualLanguage() ?? language)
    })
    return () => { cancelled = true }
  }, [i18n, user?.empresaId, user?.filialId, user?.idioma])

  const changeLanguage = (language: SupportedLanguage) => {
    chooseLanguage(language)
    void i18n.changeLanguage(language)
  }

  return (
    <div className="flex min-h-9 items-center justify-between gap-3 bg-[#111214] px-4 py-1 text-[11px] text-white md:px-6">
      <div className="hidden min-w-0 items-center gap-5 lg:flex">
        <a href="mailto:contato@goldenax.us" className="flex items-center gap-1.5 hover:text-[#e8bd55]">
          <Mail className="h-3 w-3" />
          contato@goldenax.us
        </a>
        <a href="tel:+14076978921" className="flex items-center gap-1.5 hover:text-[#e8bd55]">
          <Phone className="h-3 w-3" />
          +1 (407) 697-8921
        </a>
        <span className="flex items-center gap-1.5">
          <MapPin className="h-3 w-3" />
          Orlando &amp; Winter Garden, FL
        </span>
      </div>

      <div className="ml-auto flex items-center gap-1" role="group" aria-label="Language">
        {languages.map(({ code, country, label }) => (
          <button
            key={code}
            type="button"
            onClick={() => changeLanguage(code)}
            aria-label={label}
            aria-pressed={activeLanguage === code}
            title={label}
            className={`rounded border p-1 leading-none transition ${
              activeLanguage === code
                ? 'border-[#c99120] bg-[#c99120]/20'
                : 'border-transparent hover:border-white/40'
            }`}
          >
            <FlagIcon country={country} />
          </button>
        ))}
      </div>
    </div>
  )
}
